import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "@/config";
import { CheckCircle, XCircle, Loader2, Building2, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface PendingDealer {
    id: number;
    company: string | null;
    company_legalname: string | null;
    contact: string | null;
    contact_phone: string | null;
    email: string;
    phone: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    dealer_type_label: string | null;
    created_at: string | null;
}

export default function AdminPendingDealersPage() {
    const [dealers, setDealers] = useState<PendingDealer[]>([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState<number | null>(null);
    const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    const load = () => {
        setLoading(true);
        http.get("/admin/dealers/pending")
            .then((r) => setDealers(r.data.pending ?? []))
            .catch(() => setDealers([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const act = async (id: number, action: "approve" | "reject") => {
        setActing(id);
        setMessage(null);
        try {
            await http.post(`/admin/dealers/pending/${id}/${action}`);
            setMessage({ type: "ok", text: action === "approve" ? "Dealer approved and activated." : "Application rejected." });
            setDealers((prev) => prev.filter((d) => d.id !== id));
        } catch (e: any) {
            setMessage({ type: "err", text: e?.response?.data?.message ?? "Action failed." });
        } finally {
            setActing(null);
        }
    };

    return (
        <div className="space-y-6 p-6">
            <div>
                <h1 className="text-2xl font-semibold">Pending Dealer Applications</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {loading ? "Loading…" : `${dealers.length} application${dealers.length !== 1 ? "s" : ""} awaiting review`}
                </p>
            </div>

            {message && (
                <div className={`rounded-lg px-4 py-3 text-sm font-medium ${message.type === "ok" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : dealers.length === 0 ? (
                <Card className="py-16 text-center">
                    <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No pending applications</p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {dealers.map((d) => (
                        <Card key={d.id} className="p-5">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                        <Building2 className="h-5 w-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">{d.company ?? d.company_legalname ?? "—"}</p>
                                        {d.company_legalname && d.company_legalname !== d.company && (
                                            <p className="text-xs text-muted-foreground">Legal: {d.company_legalname}</p>
                                        )}
                                        <p className="text-sm text-muted-foreground mt-0.5">{d.email}</p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                                            {d.contact && <span className="text-xs text-gray-500">Contact: {d.contact}</span>}
                                            {(d.contact_phone || d.phone) && <span className="text-xs text-gray-500">Phone: {d.contact_phone ?? d.phone}</span>}
                                            {d.dealer_type_label && <span className="text-xs text-gray-500">Type: {d.dealer_type_label}</span>}
                                        </div>
                                        {(d.city || d.state) && (
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {[d.address, d.city, d.state, d.zip].filter(Boolean).join(", ")}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2 shrink-0">
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Clock className="h-3 w-3" />
                                        {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={acting === d.id}
                                            onClick={() => act(d.id, "reject")}
                                            className="text-red-600 border-red-200 hover:bg-red-50"
                                        >
                                            {acting === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}
                                            Reject
                                        </Button>
                                        <Button
                                            size="sm"
                                            disabled={acting === d.id}
                                            onClick={() => act(d.id, "approve")}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                        >
                                            {acting === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                                            Approve
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
